import React from "react";
import S from "./Table.styled";
import { Flex, MainInner, MainSection, motionAnimation } from "@/styles/global";
import { DetailsBig, Body, DisplayHeadingXS } from "@/styles/typography";
import { ColorOptions, IWPTitleParts } from "@/src/types";
import { isLight } from "@/src/helpers";
import { Icon, Mobile, Underline } from "@/styles/components";
import TitleParts from "@/components/global-components/TitleParts";
import { motion } from "framer-motion";

export interface TableProps {
  blockId: string;
  titleParts: IWPTitleParts;
  title: string;
  backgroundColor: ColorOptions;
  backgroundType: "card" | "fill";
  tableType: "text" | "boolean";
  columnHeaders: { header: string }[];
  rows: {
    rowHeader: string;
    rowText: string;
    columns: { cellContent?: string; boolean?: "true" | "false" }[];
  }[];
}

const Table: React.FC<TableProps> = ({
  blockId = "",
  titleParts,
  title,
  backgroundColor,
  backgroundType,
  tableType,
  columnHeaders,
  rows,
}) => {
  const textColor =
    backgroundColor === "pink"
      ? "creme"
      : backgroundColor === "yellow"
        ? "brown"
        : isLight(backgroundColor)
          ? "black"
          : "creme";

  const mostRows = Math.max(...rows.map((row) => row.columns.length));
  const maxColumns =
    mostRows > columnHeaders?.length ? mostRows : columnHeaders?.length;

  return (
    <S.Section as={motion.section} {...motionAnimation} id={blockId}>
      <S.Wrapper
        $backgroundColor={backgroundColor}
        $backgroundType={backgroundType}
      >
        <MainInner>
          <S.Table
            $backgroundColor={backgroundColor}
            $backgroundType={backgroundType}
            $pink={backgroundColor === "pink"}
          >
            <S.Container
              $backgroundType={backgroundType}
              $pink={backgroundColor === "pink"}
            >
              <S.Title $color={textColor} color={textColor} size={32}>
                {titleParts ? (
                  <TitleParts
                    titleParts={titleParts}
                    backgroundColor={backgroundColor}
                  />
                ) : (
                  title && title
                )}
              </S.Title>

              <S.TableHead>
                <S.TableRow
                  $color={textColor}
                  $pink={backgroundColor === "pink"}
                  $backgroundType={backgroundType}
                >
                  <S.TableHeader />

                  {columnHeaders?.map((header, i) => (
                    <S.TableHeader key={i} $icons={tableType === "boolean"}>
                      <DisplayHeadingXS color={textColor} size={20}>
                        {header.header}
                      </DisplayHeadingXS>
                    </S.TableHeader>
                  ))}
                </S.TableRow>
              </S.TableHead>
              <S.TableBody>
                {rows.map((row, rowIndex) => (
                  <S.TableRow
                    key={rowIndex}
                    $color={textColor}
                    $pink={backgroundColor === "pink"}
                    $backgroundType={backgroundType}
                  >
                    <S.RowHeader
                      $numberOfColumns={maxColumns}
                      $icons={tableType === "boolean"}
                    >
                      <DetailsBig
                        color={textColor}
                        size={20}
                        margin={row.rowText && "0 0 8px 0"}
                      >
                        {row.rowHeader}
                      </DetailsBig>
                      <Body color={textColor} size={16}>
                        {row.rowText}
                      </Body>
                    </S.RowHeader>
                    {row.columns.map((column, columnIndex) => (
                      <S.TableCell
                        key={columnIndex}
                        $numberOfColumns={maxColumns}
                        $color={textColor}
                        $icons={tableType === "boolean"}
                        $pink={backgroundColor === "pink"}
                      >
                        {tableType === "boolean" ? (
                          <Flex
                            $align="center"
                            $desktopJustify="center"
                            $justify="between"
                            $margin={"12px 0 0 0"}
                          >
                            <Mobile breakpoint="lg">
                              <DisplayHeadingXS color={textColor} size={16}>
                                {columnHeaders &&
                                  columnHeaders[columnIndex]?.header}
                              </DisplayHeadingXS>
                            </Mobile>
                            <Icon
                              src="Check"
                              color={
                                column.boolean === "true"
                                  ? textColor
                                  : "transparent"
                              }
                              width={24}
                            />
                          </Flex>
                        ) : (
                          <>
                            <Mobile breakpoint="lg">
                              <DisplayHeadingXS
                                color={textColor}
                                size={20}
                                margin={"12px 0 0 0"}
                              >
                                {columnHeaders &&
                                  columnHeaders[columnIndex]?.header}
                              </DisplayHeadingXS>
                            </Mobile>
                            <Body color={textColor}>{column.cellContent}</Body>
                          </>
                        )}
                      </S.TableCell>
                    ))}
                    {Array.from({
                      length: maxColumns - row.columns.length,
                    }).map((_, i) => (
                      <S.TableCell
                        key={i}
                        $numberOfColumns={maxColumns}
                        $color={textColor}
                        $icons={tableType === "boolean"}
                      >
                        <Flex $center />
                      </S.TableCell>
                    ))}
                    {rowIndex !== rows.length - 1 && (
                      <Mobile breakpoint="lg">
                        {backgroundColor !== "pink" && (
                          <Underline
                            $color={textColor}
                            $opacity={0.3}
                            $margin="32px 0 24px 0"
                          />
                        )}
                      </Mobile>
                    )}
                  </S.TableRow>
                ))}
              </S.TableBody>
            </S.Container>
          </S.Table>
        </MainInner>
      </S.Wrapper>
    </S.Section>
  );
};

export default Table;
